import React, { useState } from 'react';

import { IconButton, Typography } from '@mui/material';
import { useTheme, withStyles } from '@mui/styles';
import PropTypes from 'prop-types';
import { injectIntl, useIntl } from 'react-intl';
import { compose } from 'redux';

import ApiKeyGenerator from 'components/ApiKeyGenerator';
import DialogApiKeyGenerator from 'components/ApiKeyGenerator/DialogApiKeyGenerator';
import Avatar from 'components/AvatarV3';
import ConfirmDialog from 'components/dialogs/ConfirmDialog';
import FCList from 'components/FCList';
import LogOut from 'components/LogOut';
import { People } from 'components/SvgComponents/icons/module';
import AccountSettings from 'containers/AccountSettings';
import useAuthSession from 'hooks/useAuthSession';
import { useFetchUserAccessToApiKeyCreationQuery } from 'redux/api/user.api';
import globalMessages from 'translations/messages/global-messages';
import Analytics from 'utils/Analytics';
import localStorageUser from 'utils/localStorageUser';

import { AVATAR_HEIGHT, AVATAR_WIDTH } from '../../constants';

const styles = theme => ({
  avatar: {
    width: AVATAR_WIDTH,
    height: AVATAR_HEIGHT,
    marginRight: theme.spacing(2),
  },
  accountSettingsWrapper: {
    display: 'none',
    width: AVATAR_WIDTH + 8,
    height: AVATAR_HEIGHT + 8,
    position: 'absolute',
    borderRadius: '50%',
  },
  avatarRoot: {
    width: AVATAR_WIDTH,
    height: AVATAR_HEIGHT,
  },
  avatarWrapper: {
    [theme.breakpoints.down('xs')]: {
      padding: 0,
    },
    '&:hover $avatarRoot': {
      boxShadow: `0px 0px 0px 2px ${theme.palette.text.primary}`,
    },
    '&:hover $accountSettingsWrapper': {
      display: 'block',
    },
  },
});

function AccountSettingsButton({ classes, dataUser = {} }) {
  const { logout } = useAuthSession();
  const intl = useIntl();
  const theme = useTheme();
  const [openModalApiKey, setOpenModalApiKey] = useState(false);
  const [openModalLogout, setOpenModalLogout] = useState(false);
  const [openAccountSettingModal, setOpenAccountSettingModal] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const { data: userAccessInfo } = useFetchUserAccessToApiKeyCreationQuery();

  const handleSignOut = async () => {
    Analytics.track('users_sign_out');
    localStorageUser.cleanExpiredProjectFilter();
    await logout();
  };

  return (
    <>
      <IconButton
        className={classes.avatarWrapper}
        aria-label="Account"
        aria-describedby="account-settings"
        ref={setAnchorEl}
      >
        <Avatar
          materialUIClasses={{ root: classes.avatarRoot }}
          alt={dataUser?.media_resource?.display_name}
          src={dataUser?.media_resource?.url}
          user={dataUser}
        />

        <div className={classes.accountSettingsWrapper} />
      </IconButton>
      <FCList anchor={anchorEl} placement="bottom-start" attacheEventToAnchor>
        <FCList.ListItemButton onClick={() => setOpenAccountSettingModal(true)}>
          <People
            height={16}
            width={16}
            color={theme.palette.text.primary}
            style={{ marginRight: '8px' }}
          />

          <Typography variant="body">
            {intl.formatMessage(globalMessages.account_settings)}
          </Typography>
        </FCList.ListItemButton>
        {userAccessInfo?.has_access_to_api_key_creation && (
          <ApiKeyGenerator onClickGenerate={() => setOpenModalApiKey(!openModalApiKey)} />
        )}
        <LogOut onClickLogout={() => setOpenModalLogout(!openModalLogout)} />
      </FCList>
      {openModalLogout && (
        <ConfirmDialog
          displayCancel
          open={openModalLogout}
          title={intl.formatMessage(globalMessages.sign_out)}
          message={intl.formatMessage(globalMessages.confirm_continue)}
          submitLabel={intl.formatMessage(globalMessages.sign_out)}
          onClose={() => setOpenModalLogout(false)}
          onSubmit={handleSignOut}
        />
      )}
      {openModalApiKey && (
        <DialogApiKeyGenerator
          onCloseDialog={() => setOpenModalApiKey(!openModalApiKey)}
          onSubmitDialog={() => {}}
        />
      )}
      {openAccountSettingModal && (
        <AccountSettings
          onCloseDialog={() => setOpenAccountSettingModal(!openAccountSettingModal)}
        />
      )}
    </>
  );
}

AccountSettingsButton.propTypes = {
  classes: PropTypes.object.isRequired,
  dataUser: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
};

export default compose(
  injectIntl,
  withStyles(styles),
)(AccountSettingsButton);
